/**
 * PropertyDefsService — the per-space property schema (`propertyDefs/{spaceKey}`).
 *
 * ONE DOCUMENT PER SPACE, keyed BY the space rather than carrying a space field.
 * That makes the security rule a PATH comparison, so a caller cannot write a
 * schema for a space they don't reach at all, rather than being stopped at a
 * field check one layer in — the same shape as `userPrefs/{uid}`.
 *
 * THE RULE ENUMERATES ITS KEYS and `work` is deliberately absent: reads and
 * writes are allowed only for `personal | owners | barbara`, all gated on
 * isOwner(). If the Work space ever adopts this engine, that branch gets written
 * on purpose (isEmployeeOrAdmin to read, isAdmin to write) rather than
 * inheriting an owner-only rule by accident. `assertKnownSpace` mirrors the rule
 * client-side so a typo fails with a sentence instead of a permission error.
 *
 * WHOLE-DOCUMENT OVERWRITE, last-write-wins — the same shape as the Docs
 * autosave and for the same reason: one small document, overwritten, cannot
 * inflate or half-apply. Two owners editing the schema simultaneously would have
 * one silently win; at two people editing a household task schema that is not a
 * real conflict, and solving it properly means per-property writes, which buys
 * complexity nothing else here needs.
 *
 * Everything shape-related lives in propertyDefsModel — this file only moves the
 * document. Reads normalize on the way out, so a hand-edited console entry can
 * never reach a surface malformed.
 */

import { doc, getDoc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeDefs } from './propertyDefsModel';

const COLLECTION = 'propertyDefs';

/** Mirrors the enumerated key list in firestore.rules. */
export const SCHEMA_SPACES = ['personal', 'owners', 'barbara'];

function assertKnownSpace(spaceKey) {
	if (!SCHEMA_SPACES.includes(spaceKey)) {
		throw new Error(
			`No property schema for the '${spaceKey}' space. ` +
				`The rules allow only: ${SCHEMA_SPACES.join(', ')}.`
		);
	}
}

const refFor = (spaceKey) => {
	assertKnownSpace(spaceKey);
	return doc(db, COLLECTION, spaceKey);
};

/**
 * Live subscription to a space's property definitions.
 * A missing document is not an error — it is a space nobody has defined
 * properties for yet, and normalizes to an empty list.
 *
 * @returns {() => void} unsubscribe
 */
export function subscribeToPropertyDefs(spaceKey, onData, onError) {
	let ref;
	try {
		ref = refFor(spaceKey);
	} catch (err) {
		onError?.(err);
		return () => {};
	}

	return onSnapshot(
		ref,
		(snap) => onData(normalizeDefs(snap.exists() ? snap.data() : [])),
		(err) => onError?.(err)
	);
}

/** One-shot read, for callers that don't want a live listener. */
export async function fetchPropertyDefs(spaceKey) {
	const snap = await getDoc(refFor(spaceKey));
	return normalizeDefs(snap.exists() ? snap.data() : []);
}

/**
 * Persist the whole definition list.
 *
 * Normalized before writing as well as after reading — the model's mutators
 * already return clean lists, but this is the only door to the document and a
 * malformed schema is the kind of thing that renders a control which silently
 * does nothing. Cheap to be certain at the boundary.
 */
export async function savePropertyDefs(spaceKey, defs) {
	const ref = refFor(spaceKey);
	await setDoc(
		ref,
		{
			props: normalizeDefs(defs),
			space: spaceKey,
			updatedAt: serverTimestamp(),
		},
		{ merge: false }
	);
}

const PropertyDefsService = {
	SCHEMA_SPACES,
	subscribeToPropertyDefs,
	fetchPropertyDefs,
	savePropertyDefs,
};

export default PropertyDefsService;
